import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useCart } from '../CartContext';
import { useAuth } from '../AuthContext';
import { useAuthModal } from '../AuthModalContext';
import { supabase } from '../supabaseClient';
import './CartDrawer.css';

// 6-char alphanumeric segment - ~2.2B possibilities, collision-resistant
function randSegment() {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(36).padStart(2, '0')).join('').slice(0, 6).toUpperCase();
  }
  return Math.random().toString(36).slice(2, 8).toUpperCase();
}

async function genConfNum(type) {
  const prefix = type === 'tournament_team' ? 'TEAM' : type === 'tournament_fa' ? 'FA' : 'DROPIN';
  const table  = type === 'tournament_team' ? 'tournament_registrations'
               : type === 'tournament_fa'   ? 'tournament_free_agents'
               : 'drop_in_registrations';
  const year = new Date().getFullYear();
  for (let i = 0; i < 10; i++) {
    const c = `OVR-${prefix}-${year}-${randSegment()}`;
    const { data } = await supabase.from(table).select('id').eq('confirmation_number', c).maybeSingle();
    if (!data) return c;
  }
  // Final fallback uses full UUID - guaranteed unique
  return `OVR-${prefix}-${year}-${(typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID().slice(0, 8).toUpperCase() : Date.now().toString(36).toUpperCase()}`;
}

function parseFeeCents(feeStr) {
  // Strip everything but digits and a single decimal point - defensive
  const cleaned = (feeStr || '0').replace(/[^0-9.]/g, '');
  // If multiple dots, keep only the first
  const firstDot = cleaned.indexOf('.');
  const safe = firstDot === -1
    ? cleaned
    : cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
  const num = parseFloat(safe);
  return isNaN(num) || num < 0 ? 0 : Math.round(num * 100);
}

function fmtCAD(cents) {
  return `$${(cents / 100).toFixed(2)} CAD`;
}

// Promo codes — display + checkout-eligibility check ONLY. The actual discount is
// applied server-side in the create-checkout-session edge function; this map must
// stay in sync with it (PROMO_CODES + OWNER_EMAIL). Each code is restricted to
// specific account email(s); the owner account can redeem any code.
const OWNER_EMAIL = 'ovrleague@gmail.com';
const PROMO_CODES = {
  owen5:         { emails: ['owenchenyp@gmail.com'], amountOffCents: 500, expires: Date.parse('2026-06-29T03:59:59Z') }, // $5 off · Owen
  '100off':      { emails: ['jnguy1000@gmail.com'],  percentOff: 100,     expires: Date.parse('2026-06-30T03:59:59Z') }, // 100% comp · jnguy1000 (recovery)
  ovrambassador: { emails: ['hamzamfahmi@gmail.com'], teamShareOff: true,  expires: Date.parse('2027-01-01T05:00:00Z') }, // ambassador · equal share of team entry (perPlayer×6 ÷ roster, multi-use)
  invite10:      { emails: [], anyone: true, teamPercentOff: 10, expires: Date.parse('2026-10-26T04:00:00Z') }, // invite cards · 10% off tournament team entries · anyone · ends Oct 25 11:59 PM ET
};
// Returns the matched promo when valid, { error } when not, or null when blank.
function validatePromo(code, userEmail) {
  if (!code || !code.trim()) return null;
  const p = PROMO_CODES[code.trim().toLowerCase()];
  if (!p) return { error: 'Invalid code.' };
  if (Date.now() > p.expires) return { error: 'This code has expired.' };
  const email = (userEmail || '').trim().toLowerCase();
  if (!p.anyone && email !== OWNER_EMAIL && !p.emails.includes(email)) return { error: 'This code is not valid for your account.' };
  return p;
}

// Truncate user-supplied strings before sending to Stripe (250-char product name limit, 500-char description)
function safeStr(s, max) {
  if (typeof s !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return s.replace(/[\x00-\x1F\x7F]/g, '').trim().slice(0, max);
}

const VALID_TABLES = { tournament_registrations: true, tournament_free_agents: true, drop_in_registrations: true };

// Roll back any partially-inserted rows + FA claims if checkout fails mid-loop
async function rollback(savedSoFar) {
  for (const s of savedSoFar) {
    if (!s.registrationId || !VALID_TABLES[s.table]) continue;
    try {
      if (s.claimedFaIds && s.claimedFaIds.length) {
        await supabase.from('tournament_free_agents')
          .update({ assigned_team_id: null })
          .in('id', s.claimedFaIds)
          .eq('assigned_team_id', s.registrationId);
      }
      await supabase.from(s.table).delete().eq('id', s.registrationId).eq('payment_status', 'pending');
    } catch (e) { /* best-effort cleanup */ }
  }
}

function openEditInTournamentFlow(item, setOpen) {
  // Write item data as a draft so TournamentFlow restores it at the roster step
  try {
    localStorage.setItem('ovrRegDraft', JSON.stringify({
      step: 'roster',
      regType: 'team',
      tournKey: item.tournKey,
      team: item.team,
      _editCartItemId: item.id,
    }));
  } catch (e) { /* ignore quota */ }
  setOpen(false);
  window.dispatchEvent(new CustomEvent('ovr:edit-cart-item'));
}

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, faConflicts, checkFaConflicts, checkingConflicts } = useCart();
  const { user }          = useAuth();
  const { openAuthModal } = useAuthModal();

  const [checkingOut,   setCheckingOut]   = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const checkingOutRef = useRef(false);
  const [faInfoOpen, setFaInfoOpen] = useState({});
  const [promoInput, setPromoInput] = useState('');

  const TIMER_SECS = 15 * 60;
  const [secsLeft, setSecsLeft] = useState(TIMER_SECS);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open && items.length > 0) {
      setSecsLeft(TIMER_SECS);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setSecsLeft(s => {
          if (s <= 1) { clearInterval(timerRef.current); return 0; }
          return s - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
      setSecsLeft(TIMER_SECS);
    }
    return () => clearInterval(timerRef.current);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const timerMins    = Math.floor(secsLeft / 60);
  const timerSecs    = secsLeft % 60;
  const timerStr     = `${timerMins}:${String(timerSecs).padStart(2, '0')}`;
  const timerUrgent  = secsLeft <= 120 && secsLeft > 0;
  const timerExpired = secsLeft === 0;

  useEffect(() => {
    if (open && items.length > 0) checkFaConflicts(items);
  }, [open, items, checkFaConflicts]);

  // Real-time: re-check FA conflicts when any FA row is updated/deleted.
  // Catches the case where another captain claims an FA while this cart is open.
  useEffect(() => {
    if (!open || items.length === 0) return;
    const hasFa = items.some(i => (i.faPlayers || []).length > 0 || i.type === 'tournament_fa');
    if (!hasFa) return;

    const channel = supabase
      .channel(`cart-fa-watch-${user?.id || 'anon'}`)
      .on('postgres_changes',
          { event: '*', schema: 'public', table: 'tournament_free_agents' },
          () => { checkFaConflicts(items); })
      .subscribe();

    // Safety net: also poll every 20s in case realtime drops
    const poll = setInterval(() => checkFaConflicts(items), 20000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [open, items, checkFaConflicts, user?.id]);

  const hasConflict = items.some(i => faConflicts[i.id]?.length > 0);
  const totalCents  = items.reduce((s, i) => s + parseFeeCents(i.fee), 0);
  const teamItemsCents = items.filter(i => i.type === 'tournament_team').reduce((s, i) => s + parseFeeCents(i.fee), 0);
  const rawPromo    = validatePromo(promoInput, user?.email);
  const promo       = rawPromo && !rawPromo.error && rawPromo.teamPercentOff && teamItemsCents === 0
    ? { error: 'This code only applies to tournament team registrations.' }
    : rawPromo;
  // Ambassador "equal share": one member's split of the standard team entry
  // (perPlayer × 6 ÷ roster), taken off the first eligible team item.
  const teamItem    = items.find(i => i.type === 'tournament_team' && i.perPlayerCents && i.numPlayers);
  const teamShareOff = teamItem ? Math.round(teamItem.perPlayerCents * 6 / teamItem.numPlayers) : 0;
  const promoOff    = (promo && !promo.error)
    ? (promo.percentOff ? Math.round(totalCents * promo.percentOff / 100)
      : promo.teamPercentOff ? Math.round(teamItemsCents * promo.teamPercentOff / 100)
      : promo.teamShareOff ? Math.min(teamShareOff, Math.max(0, totalCents - 100))
      : Math.min(promo.amountOffCents, totalCents))
    : 0;
  const payCents    = Math.max(0, totalCents - promoOff);

  async function handleCheckout() {
    if (!user) { setOpen(false); openAuthModal(null, 'signup', 'checkout'); return; }
    if (hasConflict || checkingConflicts) return;
    if (checkingOutRef.current) return;
    checkingOutRef.current = true;

    setCheckingOut(true);
    setCheckoutError(null);

    // Force-refresh the token so the server never sees an expired JWT
    let liveSession = null;
    try {
      const { data: refreshed } = await supabase.auth.refreshSession();
      liveSession = refreshed?.session;
    } catch (_) {}
    if (!liveSession?.access_token) {
      // Fall back to cached session (e.g. refresh token also expired)
      const { data: { session: cached } } = await supabase.auth.getSession();
      liveSession = cached;
    }
    if (!liveSession?.user) {
      setCheckoutError('Your session expired. Please sign in again.');
      setCheckingOut(false);
      checkingOutRef.current = false;
      return;
    }

    const saved = [];

    const pendingKey = `ovr_pending_payment_${user.id}`;
    try {
      // Clean up any previously orphaned pending rows from an abandoned checkout
      const prevPending = (() => {
        try { return JSON.parse(localStorage.getItem(pendingKey) || '[]'); } catch { return []; }
      })();
      if (prevPending.length > 0) {
        for (const p of prevPending) {
          if (p.registrationId && p.table && VALID_TABLES[p.table]) {
            // Release any FAs the abandoned attempt claimed
            await supabase.from('tournament_free_agents')
              .update({ assigned_team_id: null })
              .eq('assigned_team_id', p.registrationId)
              .eq('payment_status', 'paid');
            await supabase.from(p.table).delete().eq('id', p.registrationId).eq('payment_status', 'pending').eq('user_id', user.id);
          }
        }
        localStorage.removeItem(pendingKey);
        localStorage.removeItem('ovr_pending_payment'); // legacy key cleanup
      }

      // 1. Save all registrations with payment_status: 'pending'
      for (const item of items) {
        const confNum = await genConfNum(item.type);
        let registrationId = null;
        let table = null;
        let claimedFaIds = [];

        if (item.type === 'tournament_team') {
          const namedPlayers = (item.team?.players || []).filter(p =>
            (p.firstName || p.lastName || '').trim() || (p.name || '').trim()
          );
          if (namedPlayers.length === 0) {
            throw new Error(`Team "${item.team?.name || ''}" has no named players. Please edit the registration.`);
          }

          // Clean up orphaned pending team registrations from abandoned checkouts for same user+tournament.
          // This frees any FAs that were tentatively assigned to those stale rows.
          const { data: stalePending } = await supabase
            .from('tournament_registrations')
            .select('id')
            .eq('user_id', user.id)
            .eq('season', item.tournKey)
            .eq('payment_status', 'pending');
          if (stalePending?.length) {
            const staleIds = stalePending.map(r => r.id);
            await supabase.from('tournament_free_agents')
              .update({ assigned_team_id: null })
              .in('assigned_team_id', staleIds);
            await supabase.from('tournament_registrations')
              .delete()
              .in('id', staleIds);
          }

          const { data, error } = await supabase.from('tournament_registrations').insert({
            user_id:          user.id,
            season:           item.tournKey,
            team_name:        safeStr(item.team?.name, 120),
            num_players:      namedPlayers.length,
            captain_name:     safeStr(`${item.team?.captainFirstName || ''} ${item.team?.captainLastName || ''}`.trim(), 120),
            captain_email:    safeStr(item.team?.captainEmail, 120),
            phone:            (item.team?.captainPhone || '').replace(/\D/g, ''),
            players:          namedPlayers,
            confirmation_number: confNum,
            payment_status:   'pending',
            paid:             false,
          }).select('id').single();
          if (error) throw error;
          registrationId = data.id;
          table = 'tournament_registrations';

          // Atomic FA claim - only succeeds if the FA exists and is still unassigned
          const requestedIds = (item.faPlayers || []).map(fa => fa.fa_id).filter(Boolean);
          if (requestedIds.length) {
            // First verify which FA IDs actually exist in the DB
            const { data: existing } = await supabase
              .from('tournament_free_agents')
              .select('id, assigned_team_id')
              .in('id', requestedIds);
            const existingMap = new Map((existing || []).map(r => [r.id, r]));
            // IDs that don't exist in DB at all are stale cart data — skip silently
            const validIds = requestedIds.filter(id => existingMap.has(id));
            // IDs that exist but are already claimed by another team
            const alreadyClaimed = validIds.filter(id => existingMap.get(id)?.assigned_team_id);
            if (alreadyClaimed.length > 0) {
              throw new Error(`One or more free agents on ${item.team?.name || 'your team'} were just claimed by another team. Please review your roster.`);
            }
            if (validIds.length > 0) {
              const { data: claimed, error: claimErr } = await supabase
                .from('tournament_free_agents')
                .update({ assigned_team_id: registrationId })
                .in('id', validIds)
                .is('assigned_team_id', null)
                .select('id');
              if (claimErr) throw claimErr;
              claimedFaIds = (claimed || []).map(r => r.id);
            }
          }

        } else if (item.type === 'tournament_fa') {
          // Clean up any orphaned pending rows from a previous abandoned checkout
          await supabase
            .from('tournament_free_agents')
            .delete()
            .eq('user_id', user.id)
            .eq('tournament', item.tournKey)
            .eq('payment_status', 'pending');

          // Only block if there is already a confirmed paid registration
          const { data: dup } = await supabase
            .from('tournament_free_agents')
            .select('id')
            .eq('user_id', user.id)
            .eq('tournament', item.tournKey)
            .eq('payment_status', 'paid')
            .maybeSingle();
          if (dup) {
            throw new Error('You already have a free agent registration for this tournament.');
          }
          const { data, error } = await supabase.from('tournament_free_agents').insert({
            user_id:    user.id,
            tournament: item.tournKey,
            name:       safeStr(`${item.fa?.firstName || ''} ${item.fa?.lastName || ''}`.trim(), 120),
            email:      safeStr(item.fa?.email, 120),
            phone:      (item.fa?.phone || '').replace(/\D/g, ''),
            position:   item.fa?.position,
            gender:     item.fa?.gender,
            confirmation_number: confNum,
            payment_status: 'pending',
            paid: false,
          }).select('id').single();
          if (error) throw error;
          registrationId = data.id;
          table = 'tournament_free_agents';

        } else if (item.type === 'dropin') {
          // Duplicate guard - only blocks if a paid registration exists.
          // Stale pending rows from aborted checkouts get cleaned up first.
          await supabase
            .from('drop_in_registrations')
            .delete()
            .eq('user_id', user.id)
            .eq('session_id', item.sessionId)
            .eq('payment_status', 'pending');
          const { data: existing } = await supabase
            .from('drop_in_registrations')
            .select('id')
            .eq('user_id', user.id)
            .eq('session_id', item.sessionId)
            .eq('payment_status', 'paid')
            .maybeSingle();
          if (existing) {
            throw new Error('You are already registered for this drop-in session.');
          }
          // Capacity re-check at checkout time (per-team cap)
          if (item.teamLetter) {
            const { count: teamCount } = await supabase
              .from('drop_in_registrations')
              .select('id', { count: 'exact', head: true })
              .eq('session_id', item.sessionId)
              .eq('team_letter', item.teamLetter)
              .not('payment_status', 'in', '(admin_removed,refunded,user_cancelled)');
            const teamCap = item.level === 'beg' ? 6 : 7;
            if (typeof teamCount === 'number' && teamCount >= teamCap) {
              throw new Error(`Team ${item.teamLetter} just filled up for "${item.sessionLabel || ''}". Please go back and pick another team.`);
            }
          }
          const userName = user.user_metadata?.full_name || (user.email ? user.email.split('@')[0] : 'Player');
          const { data, error } = await supabase.from('drop_in_registrations').insert({
            user_id:          user.id,
            user_name:        safeStr(userName, 120),
            user_email:       safeStr(user.email || '', 120),
            level:            item.level,
            session_id:       item.sessionId,
            session_label:    item.sessionLabel,
            session_time:     item.sessionTime,
            session_location: item.sessionLocation,
            team_letter:      item.teamLetter || null,
            position:         item.position,
            confirmation_number: confNum,
            payment_status:   'pending',
            paid:             false,
          }).select('id').single();
          if (error) throw error;
          registrationId = data.id;
          table = 'drop_in_registrations';

        } else {
          throw new Error(`Unsupported cart item type: ${item.type}`);
        }

        saved.push({
          registrationId,
          table,
          confNum,
          claimedFaIds,
          label:    item.label,
          sublabel: item.sublabel,
          fee:      item.fee,
          type:     item.type,
        });
      }

      // 2. Store for the success page
      try { localStorage.setItem(pendingKey, JSON.stringify(saved)); } catch (e) { /* ignore quota */ }

      // 3. Build Stripe line items - sanitize lengths to Stripe limits
      const stripeItems = saved.map(s => {
        const cents = parseFeeCents(s.fee);
        return {
          name:               safeStr(s.label || 'OVR Registration', 250),
          description:        safeStr(`Confirmation #: ${s.confNum}`, 500),
          amountCents:        cents > 0 ? cents : 100,
          registrationId:     s.registrationId,
          table:              s.table,
          confirmationNumber: s.confNum,
        };
      });

      const origin     = window.location.origin;
      const successUrl = `${origin}/?payment_success=1&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl  = `${origin}/?payment_cancelled=1`;

      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${liveSession.access_token}`,
            'apikey':        process.env.REACT_APP_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ items: stripeItems, successUrl, cancelUrl, promoCode: promoInput.trim() || undefined }),
        }
      );

      const result = await res.json();
      if (!res.ok || result.error) throw new Error(result.error || `Checkout failed (${res.status})`);
      if (!result.url) throw new Error('Checkout session did not return a URL.');

      // 4. Redirect to Stripe. Keep the cart populated so if the user backs out
      // of Stripe checkout without paying, their items are still here when they
      // return. PaymentSuccess clears the cart only after Stripe confirms payment.
      window.location.href = result.url;

    } catch (err) {
      // Roll back any rows + FA claims we created before the error
      await rollback(saved);
      localStorage.removeItem(pendingKey);
      setCheckoutError(err.message || 'Something went wrong. Please try again.');
      setCheckingOut(false);
      checkingOutRef.current = false;
    }
  }

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <div className="cart-overlay" onClick={() => setOpen(false)} />
      <div className="cart-drawer" role="dialog" aria-modal="true" aria-label="Cart">

        {/* Header */}
        <div className="cart-header">
          <div className="cart-header__left">
            <div className="cart-header__eyebrow">YOUR CART</div>
            <h2 className="cart-header__title">
              {items.length === 0 ? 'EMPTY' : `${items.length} ITEM${items.length !== 1 ? 'S' : ''}`}
            </h2>
          </div>
          <button className="cart-close" onClick={() => setOpen(false)} aria-label="Close cart">✕</button>
        </div>

        {/* Timer bar */}
        {items.length > 0 && (
          <div className={`cart-timer${timerUrgent ? ' cart-timer--urgent' : ''}${timerExpired ? ' cart-timer--expired' : ''}`}>
            {timerExpired
              ? <span className="cart-timer__expired-text">Spots may no longer be held - complete checkout or re-add items</span>
              : <>
                  <span className="cart-timer__label">Spots held for</span>
                  <span className="cart-timer__time">{timerStr}</span>
                </>
            }
          </div>
        )}

        {/* Body */}
        <div className="cart-body">
          {items.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty__icon">🏐</div>
              <p className="cart-empty__title">Nothing here yet</p>
              <p className="cart-empty__sub">Save a tournament or drop-in registration to review and pay.</p>
            </div>
          ) : (
            <div className="cart-items">
              {items.map(item => {
                const conflict    = faConflicts[item.id];
                const hasItemConflict = conflict?.length > 0;

                return (
                  <div key={item.id} className={`cart-item${hasItemConflict ? ' cart-item--conflict' : ''}`}>
                    <div className="cart-item__top">
                      <div className="cart-item__badge cart-item__badge--type">
                        {item.type === 'tournament_team' ? 'TEAM REG'
                          : item.type === 'tournament_fa' ? 'FREE AGENT'
                          : item.type === 'dropin'        ? 'DROP-IN'
                          : 'ITEM'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {item.type === 'tournament_team' && (
                          <button
                            style={{ background: 'transparent', border: '1px solid #2a2a2a', color: '#555', fontFamily: "'Bebas Neue', sans-serif", fontSize: 11, letterSpacing: '0.1em', padding: '3px 8px', cursor: 'pointer', transition: 'color 0.12s, border-color 0.12s' }}
                            onClick={() => openEditInTournamentFlow(item, setOpen)}
                            onMouseEnter={e => { e.currentTarget.style.color = '#9D1515'; e.currentTarget.style.borderColor = '#9D1515'; }}
                            onMouseLeave={e => { e.currentTarget.style.color = '#555'; e.currentTarget.style.borderColor = '#2a2a2a'; }}
                            aria-label="Edit roster"
                          >EDIT</button>
                        )}
                        <button className="cart-item__remove" onClick={() => removeItem(item.id)} aria-label="Remove item">✕</button>
                      </div>
                    </div>

                    <h3 className="cart-item__name">{item.label}</h3>
                    <p className="cart-item__sub">{item.sublabel}</p>
                    <div className="cart-item__price">{item.fee}</div>

                    {item.type === 'tournament_fa' && (
                      <div className="cart-item__fa-info-wrap">
                        <button
                          className="cart-item__fa-info-btn"
                          onClick={() => setFaInfoOpen(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                          aria-label="Free agent refund info"
                        >
                          <span className="cart-item__fa-info-icon">i</span>
                          <span className="cart-item__fa-info-label">Refund policy</span>
                          <span className="cart-item__fa-info-arrow">{faInfoOpen[item.id] ? '▲' : '▼'}</span>
                        </button>
                        {faInfoOpen[item.id] && (
                          <div className="cart-item__fa-info-body">
                            If you're not placed on a team 24 hours before the tournament date, your full registration fee is automatically refunded. You don't need to do anything - it's processed for you.
                          </div>
                        )}
                      </div>
                    )}

                    {hasItemConflict && (
                      <div className="cart-item__conflict">
                        <div className="cart-item__conflict-icon">⚠</div>
                        <div>
                          <p className="cart-item__conflict-title">Free agent no longer available</p>
                          <p className="cart-item__conflict-text">
                            {conflict.join(', ')} {conflict.length === 1 ? 'has' : 'have'} been claimed. Go back and select a different free agent.
                          </p>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer / Checkout */}
        {items.length > 0 && (
          <div className="cart-footer">
            {/* Promo code */}
            {totalCents > 0 && (
              <div className="cart-footer__promo">
                <input
                  className="cart-footer__promo-input"
                  type="text"
                  placeholder="PROMO CODE"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  autoCapitalize="characters"
                  spellCheck={false}
                />
                {promo?.error && promoInput.trim() && (
                  <div className="cart-footer__promo-msg cart-footer__promo-msg--err">{promo.error}</div>
                )}
                {promoOff > 0 && (
                  <div className="cart-footer__promo-msg cart-footer__promo-msg--ok">Code applied · −{fmtCAD(promoOff)}</div>
                )}
              </div>
            )}

            {/* Total */}
            {totalCents > 0 && (
              <div className="cart-footer__total-row">
                <span className="cart-footer__total-label">TOTAL</span>
                <span className="cart-footer__total-val">
                  {promoOff > 0 && (
                    <span style={{ textDecoration: 'line-through', opacity: 0.45, marginRight: 8, fontWeight: 400 }}>{fmtCAD(totalCents)}</span>
                  )}
                  {fmtCAD(payCents)}
                </span>
              </div>
            )}

            {checkoutError && (
              <div className="cart-footer__error">{checkoutError}</div>
            )}

            <button
              className="cart-footer__checkout-btn"
              onClick={handleCheckout}
              disabled={checkingOut || hasConflict || checkingConflicts}
            >
              {checkingOut ? 'PROCESSING...' : !user ? 'SIGN IN TO CHECKOUT →' : `PAY ${totalCents > 0 ? fmtCAD(payCents) : ''} →`}
            </button>

            <div className="cart-footer__email-note">
              Receipt sent from <strong>noreply@ovrleague.com</strong>. Check spam if you don't see it.
            </div>

            <div className="cart-footer__secure">
              <svg width="11" height="13" viewBox="0 0 11 13" fill="none">
                <path d="M5.5 0L1 2v4c0 2.8 1.9 5.4 4.5 6C10.1 11.4 10 8.8 10 6V2L5.5 0z" fill="rgba(255,255,255,0.2)" />
              </svg>
              Secure payment via Stripe · Apple Pay supported
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
